import type { PanelGeneratedHttpPort, PanelLiveScreenId, PanelRememberedScreenId, PanelScreenMemory, PanelScreenMemoryLease } from '../foundation/runtime-port';

type MemoryEntry = Readonly<{ value: unknown; release: (() => void) | undefined }>;

/**
 * The most reads one screen keeps: a manager going through periods holds the last few, never every range tried.
 * @internal Exported for its spec.
 */
export const SCREEN_MEMORY_QUERIES = 8;

/**
 * docs/decisions/2026-10-17-staff-boards-remember-their-last-board.md · what screens keep between mounts: one value per
 * screen and query (the same screen may hold several polled reads), under one principal, restaurant and grants in one
 * screen lifetime. `authorityChanged` and `forget` drop every value and retire the leases taken before, so a mount of
 * an earlier person or grant can keep nothing.
 */
export class ScreenMemoryStore {
  private readonly values = new Map<string, MemoryEntry>();
  private generation = 0;
  private authority: string | null = null;

  /** The memory of one screen in the screen lifetime `isLifetimeCurrent` answers for. */
  memoryFor<Value>(screen: PanelLiveScreenId, isLifetimeCurrent: () => boolean): PanelScreenMemory<Value> {
    return { lease: (query = '') => this.lease(`${screen}\u0000${query}`, isLifetimeCurrent) as PanelScreenMemoryLease<Value> };
  }

  private lease(key: string, isLifetimeCurrent: () => boolean): PanelScreenMemoryLease<unknown> {
    const generation = this.generation;
    const current = () => isLifetimeCurrent() && generation === this.generation && this.authority !== null;
    return {
      recalled: current() ? this.values.get(key)?.value ?? null : null,
      keep: (value, release) => {
        if (!current()) return false;
        const previous = this.values.get(key);
        // Written last, so the oldest of the screen is the first of the map.
        this.values.delete(key);
        this.values.set(key, { value, release });
        if (previous !== undefined && previous.release !== release) previous.release?.();
        this.evictOldest(key);
        return true;
      },
    };
  }

  private evictOldest(key: string): void {
    const prefix = key.slice(0, key.indexOf('\u0000') + 1);
    const ofScreen = [...this.values.keys()].filter((candidate) => candidate.startsWith(prefix));
    for (const oldest of ofScreen.slice(0, Math.max(0, ofScreen.length - SCREEN_MEMORY_QUERIES))) {
      const entry = this.values.get(oldest);
      this.values.delete(oldest);
      entry?.release?.();
    }
  }

  /** The principal and grants a value is read under; null without a screen session. */
  authorityChanged(authority: string | null): void {
    if (authority === this.authority) return;
    this.authority = authority;
    this.forget();
  }

  forget(): void {
    this.generation += 1;
    const dropped = [...this.values.values()];
    this.values.clear();
    for (const entry of dropped) entry.release?.();
  }
}

export const REMEMBERED_SCREENS: ReadonlySet<PanelLiveScreenId> = new Set<PanelRememberedScreenId>(['cocina', 'servicio', 'analytics']);
/** What each remembering screen's client may keep, by client: a screen binds its ports from its client alone. */
export const screenMemories = new WeakMap<PanelGeneratedHttpPort, PanelScreenMemory<unknown>>();
/**
 * docs/decisions/2026-10-17-staff-boards-remember-their-last-board.md · the memory of a staff board's client, or null
 * for every other screen (the TPV keeps none) and for a client this runtime did not hand out.
 */
export function screenMemoryOf<Value>(client: PanelGeneratedHttpPort): PanelScreenMemory<Value> | null {
  return (screenMemories.get(client) as PanelScreenMemory<Value> | undefined) ?? null;
}
